import { Controller } from "@hotwired/stimulus"
import { DirectUpload } from "@rails/activestorage"

export default class extends Controller {
  static targets = ["input", "progressBar", "dropArea", "fileNameDisplay", "previewContainer", "container"]
  static values = {
    uploadUrl: { type: String, default: "/rails/active_storage/direct_uploads" }
  }

  connect() {
    this.dragCounter = 0
    this.uploadComplete = false
    this.uploadedBlobId = null
    
    if (this.hasDropAreaTarget) {
      this.dropAreaTarget.addEventListener("dragenter", this.dragEnter.bind(this))
      this.dropAreaTarget.addEventListener("dragleave", this.dragLeave.bind(this))
      this.dropAreaTarget.addEventListener("dragover", this.dragOver.bind(this))
      this.dropAreaTarget.addEventListener("drop", this.drop.bind(this))
    }
    
    if (this.hasInputTarget) {
      this.inputTarget.addEventListener("change", this.handleFileSelect.bind(this))
    }
  }

  disconnect() {
    if (this.hasDropAreaTarget) {
      this.dropAreaTarget.removeEventListener("dragenter", this.dragEnter)
      this.dropAreaTarget.removeEventListener("dragleave", this.dragLeave)
      this.dropAreaTarget.removeEventListener("dragover", this.dragOver)
      this.dropAreaTarget.removeEventListener("drop", this.drop)
    }
    
    if (this.hasInputTarget) {
      this.inputTarget.removeEventListener("change", this.handleFileSelect)
    }
  }

  dragEnter(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dragCounter++
    this.dropAreaTarget.classList.add("border-blue-500", "bg-blue-50")
  }

  dragLeave(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dragCounter--
    if (this.dragCounter === 0) {
      this.dropAreaTarget.classList.remove("border-blue-500", "bg-blue-50")
    }
  }

  dragOver(e) {
    e.preventDefault()
    e.stopPropagation()
  }

  drop(e) {
    e.preventDefault()
    e.stopPropagation()
    this.dragCounter = 0
    this.dropAreaTarget.classList.remove("border-blue-500", "bg-blue-50")
    
    const files = e.dataTransfer.files
    if (files.length > 0) {
      this.inputTarget.files = files
      this.handleFileSelect({ target: { files: files } })
    }
  }

  handleFileSelect(e) {
    const files = e.target.files
    if (files.length > 0) {
      const file = files[0]
      this.uploadFile(file)
      this.createFilePreview(file)
    }
  }

  createFilePreview(file) {
    this.uploadComplete = false
    this.removeExistingBlob()
    
    if (this.hasFileNameDisplayTarget) {
      this.fileNameDisplayTarget.innerHTML = ""
      
      const fileInfoContainer = document.createElement('div')
      fileInfoContainer.className = 'flex items-center py-2 px-2 bg-white rounded-md shadow-sm border border-gray-200 text-gray-700 w-full'
      
      // Ajouter l'icône selon le type de fichier
      const icon = document.createElement('i')
      const fileType = this.getFileType(file)
      
      switch (fileType) {
        case 'pdf': icon.className = 'fa-solid fa-file-pdf text-red-500 mr-2 text-base'; break
        case 'image': icon.className = 'fa-solid fa-file-image text-green-500 mr-2 text-base'; break
        case 'word': icon.className = 'fa-solid fa-file-word text-blue-500 mr-2 text-base'; break
        case 'excel': icon.className = 'fa-solid fa-file-excel text-green-500 mr-2 text-base'; break
        case 'text': icon.className = 'fa-solid fa-file-lines text-gray-500 mr-2 text-base'; break
        default: icon.className = 'fa-solid fa-file text-gray-500 mr-2 text-base'
      }
      
      fileInfoContainer.appendChild(icon)
      
      const fileNameElem = document.createElement('span')
      fileNameElem.className = 'flex-1 truncate text-xs font-medium'
      fileNameElem.textContent = file.name
      fileInfoContainer.appendChild(fileNameElem)
      
      const statusIndicator = document.createElement('div')
      statusIndicator.className = 'ml-2 flex items-center'
      statusIndicator.innerHTML = '<div class="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>'
      statusIndicator.setAttribute('data-status', 'uploading')
      fileInfoContainer.appendChild(statusIndicator)
      
      const removeButton = document.createElement('button')
      removeButton.type = 'button'
      removeButton.className = 'ml-2 text-red-500 hover:text-red-700 focus:outline-none hidden'
      removeButton.innerHTML = '<i class="fa-solid fa-trash-alt"></i>'
      removeButton.setAttribute('data-action', 'click->file-dropzone#removeFile')
      removeButton.setAttribute('title', 'Supprimer ce fichier')
      fileInfoContainer.appendChild(removeButton)
      
      this.fileNameDisplayTarget.appendChild(fileInfoContainer)
      
      this.statusIndicator = statusIndicator
      this.removeButton = removeButton
      
      // Créer une prévisualisation si c'est une image
      if (fileType === 'image' && this.hasPreviewContainerTarget) {
        const reader = new FileReader()
        reader.onload = (e) => {
          this.previewContainerTarget.innerHTML = ''
          
          const imgWrapper = document.createElement('div')
          imgWrapper.className = 'relative w-20 h-20 overflow-hidden rounded-md border border-gray-200 shadow-sm flex items-center justify-center bg-gray-100 mx-auto'
          
          const img = document.createElement('img')
          img.src = e.target.result
          img.className = 'object-contain max-w-full max-h-full'
          
          imgWrapper.appendChild(img)
          this.previewContainerTarget.appendChild(imgWrapper)
        }
        reader.readAsDataURL(file)
      } else if (this.hasPreviewContainerTarget) {
        this.previewContainerTarget.innerHTML = ''
      }
    }
  }

  getFileType(file) {
    const mimeType = file.type
    const fileName = file.name.toLowerCase()
    
    // Vérifier d'abord par mimetype
    if (mimeType.startsWith('image/')) return 'image'
    if (mimeType === 'application/pdf') return 'pdf'
    
    // Les fichiers Excel peuvent avoir différents mimetypes
    const excelMimeTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.oasis.opendocument.spreadsheet'
    ]
    if (excelMimeTypes.includes(mimeType) || fileName.endsWith('.xlsx') || fileName.endsWith('.xls') || fileName.endsWith('.ods')) {
      return 'excel'
    }
    
    // Fichiers Word
    const wordMimeTypes = [
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.oasis.opendocument.text'
    ]
    if (wordMimeTypes.includes(mimeType) || fileName.endsWith('.doc') || fileName.endsWith('.docx') || fileName.endsWith('.odt')) {
      return 'word'
    }
    
    // Fichiers texte
    if (mimeType.includes('text/') || fileName.endsWith('.txt')) return 'text'
    
    // Par défaut
    return 'other'
  }

  uploadFile(file) {
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "0%"
      this.progressBarTarget.parentElement.classList.remove("hidden")
    }
    
    const directUpload = new DirectUpload(file, this.uploadUrlValue, {
      directUploadWillStoreFileWithXHR: (xhr) => {
        xhr.upload.addEventListener("progress", (event) => this.progressUpdated(event))
      }
    })
    
    directUpload.create((error, blob) => {
      if (error) {
        console.error(error)
        if (this.statusIndicator) {
          this.statusIndicator.innerHTML = '<i class="fa-solid fa-circle-xmark text-red-500"></i>'
          this.statusIndicator.setAttribute('data-status', 'error')
        }
      } else {
        this.uploadedBlobId = blob.signed_id
        this.createHiddenInput(blob.signed_id)
        
        if (this.statusIndicator) {
          this.statusIndicator.innerHTML = '<i class="fa-solid fa-circle-check text-green-500"></i>'
          this.statusIndicator.setAttribute('data-status', 'complete')
        }
        
        if (this.removeButton) {
          this.removeButton.classList.remove('hidden')
        }
        
        this.uploadComplete = true
        
        if (this.hasProgressBarTarget) {
          setTimeout(() => {
            this.progressBarTarget.parentElement.classList.add("hidden")
          }, 1000)
        }
      }
    })
  }

  createHiddenInput(blobId) {
    this.removeExistingBlob()
    
    // Trouver le formulaire parent au lieu d'utiliser formTarget
    const form = this.element.closest('form')
    if (!form) {
      console.error("Impossible de trouver le formulaire parent")
      return
    }
    
    const hiddenField = document.createElement('input')
    hiddenField.setAttribute("type", "hidden")
    hiddenField.setAttribute("value", blobId)
    hiddenField.name = this.inputTarget.name
    hiddenField.classList.add('active-storage-blob-id')
    
    form.appendChild(hiddenField)
  }
  
  removeExistingBlob() {
    // Trouver le formulaire parent au lieu d'utiliser formTarget
    const form = this.element.closest('form')
    if (!form) {
      console.error("Impossible de trouver le formulaire parent")
      return
    }
    
    const existingHiddenInputs = form.querySelectorAll(`.active-storage-blob-id`)
    if (existingHiddenInputs.length > 0) {
      existingHiddenInputs.forEach(input => input.remove())
    }
    this.uploadedBlobId = null
  }

  removeFile(event) {
    event.preventDefault()
    
    this.removeExistingBlob()
    this.inputTarget.value = ""
    
    if (this.hasFileNameDisplayTarget) {
      this.fileNameDisplayTarget.innerHTML = ""
    }
    
    if (this.hasPreviewContainerTarget) {
      this.previewContainerTarget.innerHTML = ""
    }
    
    if (this.hasProgressBarTarget) {
      this.progressBarTarget.style.width = "0%"
      this.progressBarTarget.parentElement.classList.add("hidden")
    }
    
    this.uploadComplete = false
  }

  progressUpdated(event) {
    if (this.hasProgressBarTarget && event.lengthComputable) {
      const progress = (event.loaded / event.total) * 100
      this.progressBarTarget.style.width = `${progress}%`
    }
  }
} 